import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MarkdownView from "./MarkdownView";
import AnswerDisclaimer from "./AnswerDisclaimer";
import EditionBadge from "./EditionBadge";
import "./QAChat.css";
import API_BASE_URL from "../config";
import { apiFetchJson } from "../apiClient";
import { citationFromEvent, linkCitations } from "../utils/citations";

// If the server doesn't confirm a stop within this window (e.g. it crashed or
// the response is already fully buffered), fall back to aborting the fetch
// directly rather than leaving the UI stuck in a loading state forever.
const STOP_SAFETY_TIMEOUT_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_STOP_SAFETY_TIMEOUT_MS) ||
      process.env.REACT_APP_STOP_SAFETY_TIMEOUT_MS,
  ) || 5000;

// BUG-08 (prod_accuracy_test_2026-09/BUGS_FOUND.md): a stream can occasionally
// stall with zero SSE events (observed: 5+ minutes stuck at "Expanding
// query…", no error surfaced). This is an IDLE timeout, reset on every chunk
// received — not a total-duration cap — so a normal answer that legitimately
// takes minutes across many status updates is never cut off; only a genuine
// silent stall triggers it.
const STREAM_IDLE_TIMEOUT_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_STREAM_IDLE_TIMEOUT_MS) ||
      process.env.REACT_APP_STREAM_IDLE_TIMEOUT_MS,
  ) || 120000;

function cleanDocName(filename) {
  return filename
    .replace(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
      "",
    )
    .replace(/\.pdf$/i, "");
}

export function SourceItem({ src, activeSeq = null }) {
  const active = activeSeq !== null;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const itemRef = useRef(null);
  // A click on the answer's [n] marker (feature 2) opens and reveals source n.
  useEffect(() => {
    if (!active) return;
    setOpen(true);
    if (itemRef.current && itemRef.current.scrollIntoView) {
      itemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [active, activeSeq]);
  const hasDocument = Boolean(src.document);
  const displayName = hasDocument ? cleanDocName(src.document) : src.title;
  const href = hasDocument
    ? `${API_BASE_URL}/documents/${encodeURIComponent(src.document)}#page=${src.page_number}`
    : null;
  const hasImages = src.images && src.images.length > 0;
  const pagePreviewUrl = src.preview_url ? `${API_BASE_URL}${src.preview_url}` : null;
  const hasExpandable = src.preview || hasImages || pagePreviewUrl;

  return (
    <li ref={itemRef} className={active ? "source-item source-item--active" : "source-item"}>
      <div className="source-header">
        {Number.isInteger(src.citation_id) && (
          <span className="source-number">[{src.citation_id}]</span>
        )}
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            <span className="source-name">{displayName}</span>{" "}
            <span dir="auto">
              — {src.citation_label || t("chat.pageFallback", { page: src.page_number })}
            </span>
          </a>
        ) : (
          <span className="source-name">{displayName}</span>
        )}
        <EditionBadge edition={src.edition} source={src.edition_source} />
        {hasExpandable && (
          <button className="source-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? "▼" : "▶"} {t("chat.preview")}
            {hasImages ? ` · ${t("chat.imageCount", { count: src.images.length })}` : ""}
          </button>
        )}
      </div>
      {src.chapter && <small>{src.chapter}</small>}
      {open && (
        <div className="source-preview">
          {pagePreviewUrl && (
            <a href={pagePreviewUrl} target="_blank" rel="noreferrer">
              <img
                className="source-page-image"
                loading="lazy"
                src={pagePreviewUrl}
                alt={t("chat.pagePreviewAlt", { page: src.page_number })}
              />
            </a>
          )}
          {src.preview && <div className="ds-markdown"><MarkdownView>{src.preview}</MarkdownView></div>}
          {hasImages && (
            <div className="source-images">
              {src.images.map((img, i) => (
                <a
                  key={i}
                  href={`${API_BASE_URL}${img.url}`}
                  target="_blank"
                  rel="noreferrer"
                  title={img.caption || ""}
                >
                  <img
                    src={`${API_BASE_URL}${img.url}`}
                    alt={img.caption || t("chat.imageAltFallback", { number: i + 1 })}
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function SourcesPanel({ sources, activeCitation = null }) {
  // activeCitation: {id, seq} of the last clicked [n] marker; seq re-reveals
  // the same source on a repeated click.
  const seqFor = (src) =>
    activeCitation && src.citation_id === activeCitation.id ? activeCitation.seq : null;
  const { t } = useTranslation();
  if (!sources || sources.length === 0) return null;
  // src.used is set server-side (see qa.py's _finalize_node) by checking
  // whether the answer actually quoted a verbatim span of that source's
  // text. Only an explicit `false` demotes a source to "considered but not
  // cited" — undefined/null (unknown, e.g. no preview to check, or citation
  // highlighting disabled) stays in the main list rather than risk hiding a
  // legitimately-used-but-paraphrased source.
  const used = sources.filter((src) => src.used !== false);
  const notCited = sources.filter((src) => src.used === false);
  return (
    <div className="sources">
      <h4>{t("chat.sources")}</h4>
      <ul>
        {used.map((src, i) => (
          <SourceItem key={i} src={src} activeSeq={seqFor(src)} />
        ))}
      </ul>
      {notCited.length > 0 && (
        <details
          className="sources-considered"
          open={notCited.some((src) => seqFor(src) !== null) || undefined}
        >
          <summary>{t("chat.moreNotCited", { count: notCited.length })}</summary>
          <ul>
            {notCited.map((src, i) => (
              <SourceItem key={i} src={src} activeSeq={seqFor(src)} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function StatusLog({ log, isStreaming }) {
  const { t } = useTranslation();
  if (!log || log.length === 0) return null;
  return (
    <details className="status-log" open={isStreaming}>
      <summary className="status-log-summary">
        {isStreaming ? (
          <>
            <span className="stream-status-dot" />
            {log[log.length - 1]}
          </>
        ) : (
          t("chat.steps", { count: log.length })
        )}
      </summary>
      <ul className="status-log-list">
        {log.map((entry, i) => (
          <li
            key={i}
            className={i === log.length - 1 && isStreaming ? "active" : ""}
          >
            {entry}
          </li>
        ))}
      </ul>
    </details>
  );
}

function IncompleteAnswerBanner({ topics }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasTopics = topics && topics.length > 0;
  return (
    <div className="incomplete-answer-banner">
      <span>⚠ {t("chat.coverageWarning")}</span>
      {hasTopics && (
        <button
          type="button"
          className="incomplete-answer-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "▼" : "▶"} {t("chat.topicsNotResolved", { count: topics.length })}
        </button>
      )}
      {open && hasTopics && (
        <ul className="incomplete-answer-topics">
          {topics.map((topic, i) => (
            <li key={i}>{topic}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// RN-326: rate an answer (helpful / partial / not helpful), with an optional
// free-text correction for the latter two — mirrors IncompleteAnswerBanner's
// open/close disclosure pattern. Only rendered once a message has a stable
// message_id (streaming turns don't have one; finalize, no_pages and the
// stopped path in api/qa.py mint one).
function FeedbackControls({ messageId, threadId }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(null); // null | "helpful" | "partial" | "not_helpful"
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const pickRating = (value) => {
    if (submitted || submitting) return;
    setRating(value);
    setError("");
    if (value === "helpful") {
      submit(value, "");
    } else {
      setShowComment(true);
    }
  };

  const submit = async (value, commentText) => {
    setSubmitting(true);
    setError("");
    try {
      await apiFetchJson("/api/v1/feedback", {
        method: "POST",
        body: JSON.stringify({
          message_id: messageId,
          thread_id: threadId,
          rating: value,
          comment: commentText || undefined,
        }),
      });
      setSubmitted(true);
      setShowComment(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <div className="feedback-controls feedback-submitted">{t("chat.feedbackThanks")}</div>;
  }

  const ratingOptions = [
    { value: "helpful", label: t("chat.feedbackHelpful") },
    { value: "partial", label: t("chat.feedbackPartial") },
    { value: "not_helpful", label: t("chat.feedbackNotHelpful") },
  ];

  return (
    <div className="feedback-controls">
      <div className="feedback-buttons">
        <span className="feedback-label">{t("chat.feedbackPrompt")}</span>
        {ratingOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`feedback-btn${rating === opt.value ? " active" : ""}`}
            disabled={submitting}
            onClick={() => pickRating(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {showComment && (
        <div className="feedback-comment-box">
          <textarea
            className="feedback-comment-input"
            placeholder={t("chat.feedbackCorrectionPlaceholder")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
          />
          <button
            type="button"
            className="feedback-comment-submit"
            disabled={submitting}
            onClick={() => submit(rating, comment.trim())}
          >
            {submitting ? t("chat.sending") : t("chat.submit")}
          </button>
        </div>
      )}
      {error && <div className="feedback-error">{error}</div>}
    </div>
  );
}

function AssistantMessage({ msg, statusLog, threadId }) {
  const { t } = useTranslation();
  const isStreaming = msg.streaming;
  const hasContent = Boolean(msg.content);
  const [activeCitation, setActiveCitation] = useState(null);
  // Sources arrive with the final answer, so markers link only once done.
  const content = useMemo(
    () => (isStreaming ? msg.content : linkCitations(msg.content, msg.sources)),
    [isStreaming, msg.content, msg.sources],
  );
  const onAnswerClick = (event) => {
    const n = citationFromEvent(event);
    if (n !== null) setActiveCitation((prev) => ({ id: n, seq: (prev ? prev.seq : 0) + 1 }));
  };
  return (
    <div className={`message assistant${isStreaming ? " streaming" : ""}`}>
      <div className="message-content">
        <StatusLog log={statusLog} isStreaming={isStreaming} />
        {hasContent && (
          <div className="chat-document ds-markdown" onClick={onAnswerClick}>
            <MarkdownView>{content}</MarkdownView>
          </div>
        )}
        {isStreaming && hasContent && <span className="typing-cursor" />}
        {!isStreaming && <AnswerDisclaimer text={msg.disclaimer} status={msg.status} />}
      </div>
      {!isStreaming && msg.stopped && (
        <div className="generation-stopped-indicator">{t("chat.generationStopped")}</div>
      )}
      {!isStreaming && msg.may_be_incomplete && (
        <IncompleteAnswerBanner topics={msg.unresolved_topics} />
      )}
      {!isStreaming && <SourcesPanel sources={msg.sources} activeCitation={activeCitation} />}
      {!isStreaming && msg.message_id && (
        <FeedbackControls messageId={msg.message_id} threadId={threadId} />
      )}
      {!isStreaming && msg.tools_used && msg.tools_used.length > 0 && (
        <div className="tools-info">
          <details>
            <summary>{t("chat.toolsUsed", { count: msg.tools_used.length })}</summary>
            <ul>
              {msg.tools_used.map((tool, tidx) => (
                <li key={tidx}>
                  <strong>{tool.tool}</strong> ({JSON.stringify(tool.arguments)}
                  )
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}

// ── Main QAChat Component ───────────────────────────────────────────────────

function QAChat({
  database = "Betonbau",
  threadId,
  initialQuestion,
  onSeedConsumed,
  onThreadCreated,
  onThreadUnavailable,
  onConversationsRefresh,
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [convLoading, setConvLoading] = useState(false);
  const [statusLog, setStatusLog] = useState([]);
  const statusLogRef = useRef([]);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Set from the "stream_id" SSE event at the start of every request; used to
  // address POST /qa/stream/{stream_id}/stop. Reset to null whenever a stream
  // ends (done/stopped/error) or a new one starts.
  const streamIdRef = useRef(null);
  // Distinguishes a user-clicked-Stop abort (finalize the bubble with a
  // "stopped" indicator) from the pre-existing thread-switch abort (silently
  // discard — the user has already navigated away from that message).
  const stoppedByUserRef = useRef(false);
  // Set when OUR idle timer aborted the stream (the server sent nothing for
  // STREAM_IDLE_TIMEOUT_MS): unlike a thread-switch abort, the user must be told.
  const idleTimedOutRef = useRef(false);
  // Tracks when threadId changes because WE created a new thread during streaming
  // (vs. the user selecting an existing one) — avoids re-fetching messages we already have.
  const threadCreatedByStream = useRef(false);
  // The current threadId, readable from the (memoized) stream handler.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => {
    scrollToBottom();
  }, [messages, statusLog]);

  // Seed the input from a "Ask about this" pivot, then tell the parent to clear the
  // seed so it can't re-inject later or overwrite the user's own input.
  useEffect(() => {
    if (initialQuestion) {
      setQuestion(initialQuestion);
      onSeedConsumed?.();
    }
  }, [initialQuestion, onSeedConsumed]);

  // Load messages whenever the threadId prop changes (user selected a conversation
  // or Dashboard restored a saved thread on mount).
  useEffect(() => {
    if (threadCreatedByStream.current) {
      // We just created this thread during streaming — messages already in state.
      threadCreatedByStream.current = false;
      return;
    }

    // Abort any in-progress stream before switching threads.
    abortControllerRef.current?.abort();
    setLoading(false);

    if (!threadId) {
      setMessages([]);
      setStatusLog([]);
      statusLogRef.current = [];
      return;
    }

    let cancelled = false;
    setConvLoading(true);
    fetch(`${API_BASE_URL}/api/v1/conversations/${threadId}/messages`, {
      headers: new Headers({
        "ngrok-skip-browser-warning": "69420",
      }),
    })
      .then((res) => {
        // 404: the conversation no longer exists or belongs to another account
        // (the browser remembers the last thread per workspace, whoever signed
        // in since). Forget it so the next question starts a fresh thread
        // instead of failing with the same error.
        if (res.status === 404 && !cancelled) onThreadUnavailable?.(threadId);
        return res.ok ? res.json() : null;
      })
      .then((raw) => {
        if (cancelled || !raw) return;
        setMessages(
          raw.map((m) => ({
            role: m.role,
            content: m.content,
            streaming: false,
            tools_used: [],
            sources: m.sources || [],
            may_be_incomplete: m.may_be_incomplete || false,
            stopped: m.stopped || false,
            message_id: m.message_id || null,
            disclaimer: m.disclaimer || "",
            status: m.status || null,
            statusLog: m.status_log || [],
          })),
        );
        setStatusLog([]);
        statusLogRef.current = [];
        // A question without an answer yet: its answer is still running (we
        // left mid-answer) or just finished — re-attach to it.
        if (raw.length && raw[raw.length - 1].role === "user") {
          attachToAnswer(threadId, () => cancelled);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load conversation history:", err);
      })
      .finally(() => {
        if (!cancelled) setConvLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [threadId]);

  // Unmounting mid-answer (the user opened another tab) only closes our
  // connection: the answer keeps running server-side and this chat
  // re-attaches to it when the thread is shown again. Only Stop ends it.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // ── Stream event handler ───────────────────────────────────────────────

  // Make the parent show and remember the thread an answer belongs to. Only a
  // NEW id may set threadCreatedByStream: an unchanged id never re-runs the
  // load effect, so the flag would stay set and swallow the next real switch.
  const adoptThread = useCallback(
    (id) => {
      if (!id || id === threadIdRef.current) return;
      threadCreatedByStream.current = true;
      threadIdRef.current = id;
      onThreadCreated(id);
    },
    [onThreadCreated],
  );

  const handleStreamEvent = useCallback(
    (event) => {
      if (event.type === "stream_id") {
        streamIdRef.current = event.stream_id;
        // Known before the answer is: a new chat is remembered (and listed)
        // right away, so leaving mid-answer and coming back finds it.
        if (event.thread_id && event.thread_id !== threadIdRef.current) {
          adoptThread(event.thread_id);
          onConversationsRefresh();
        }
      } else if (event.type === "snapshot") {
        // Re-attached to a running answer: its progress so far, then live events.
        streamIdRef.current = event.stream_id;
        // An answer still in its first stage has no status yet: keep showing
        // "Reconnecting…" rather than an empty, silent bubble.
        const log = event.status_log?.length ? event.status_log : statusLogRef.current;
        statusLogRef.current = log;
        setStatusLog(log);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming) {
            return [...prev.slice(0, -1), { ...last, content: event.content || "" }];
          }
          return prev;
        });
      } else if (event.type === "status") {
        setStatusLog((prev) => {
          // The server repeats a status as a keep-alive while tokens stream:
          // show a step once, not once per repeat.
          if (prev[prev.length - 1] === event.message) return prev;
          const next = [...prev, event.message];
          statusLogRef.current = next;
          return next;
        });
      } else if (event.type === "revision_start") {
        // A refine pass replaces the whole answer rather than appending to it
        // — reset the streaming bubble to empty so new tokens don't silently
        // overwrite mid-word; the "Refining answer (pass N)..." status line
        // (a regular "status" event, dispatched just before this one) is what
        // makes the reset visible and explained rather than looking like a bug.
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming) {
            return [...prev.slice(0, -1), { ...last, content: "" }];
          }
          return prev;
        });
      } else if (event.type === "token") {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming) {
            return [
              ...prev.slice(0, -1),
              { ...last, content: last.content + event.content },
            ];
          }
          return prev;
        });
      } else if (event.type === "error") {
        streamIdRef.current = null;
        setMessages((prev) => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          const errMsg = {
            role: "assistant",
            content: t("chat.errorPrefix", { message: event.message }),
            streaming: false,
            tools_used: [],
            // The failed turn is audited too: show its disclaimer and status.
            disclaimer: event.disclaimer || "",
            status: event.status || "error",
            message_id: event.message_id,
          };
          if (last?.streaming) return [...msgs.slice(0, -1), errMsg];
          return [...msgs, errMsg];
        });
      } else if (event.type === "done") {
        streamIdRef.current = null;
        // The server's own step list wins over the client's: the client starts
        // its list with a synthetic "Connecting…" line the server never sent,
        // so using the client's list would count one step more than the same
        // answer shows after a reload.
        const finalLog = event.status_log?.length ? event.status_log : statusLogRef.current;
        adoptThread(event.thread_id);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming) {
            return [
              ...prev.slice(0, -1),
              {
                role: "assistant",
                content: event.answer,
                tools_used: event.tools_used || [],
                sources: event.sources || [],
                may_be_incomplete: event.may_be_incomplete || false,
                unresolved_topics: event.unresolved_topics || [],
                message_id: event.message_id || null,
                disclaimer: event.disclaimer || "",
                status: event.status || null,
                statusLog: finalLog,
                streaming: false,
              },
            ];
          }
          return prev;
        });
        setLoading(false);
        onConversationsRefresh();
      } else if (event.type === "stopped") {
        streamIdRef.current = null;
        // The server's own step list wins over the client's — see the "done"
        // branch above.
        const finalLog = event.status_log?.length ? event.status_log : statusLogRef.current;
        adoptThread(event.thread_id);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.streaming) {
            return [
              ...prev.slice(0, -1),
              {
                role: "assistant",
                content: event.partial_answer,
                tools_used: [],
                sources: [],
                stopped: true,
                message_id: event.message_id || null,
                disclaimer: event.disclaimer || "",
                status: event.status || null,
                statusLog: finalLog,
                streaming: false,
              },
            ];
          }
          return prev;
        });
        setLoading(false);
        onConversationsRefresh();
      }
    },
    [adoptThread, onConversationsRefresh, t],
  );

  // ── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    const currentQuestion = question;
    setStatusLog([]);
    statusLogRef.current = [];
    streamIdRef.current = null;
    stoppedByUserRef.current = false;
    idleTimedOutRef.current = false;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: currentQuestion },
      { role: "assistant", content: "", streaming: true, tools_used: [] },
    ]);

    setQuestion("");
    setLoading(true);
    setStatusLog([t("chat.connecting")]);

    await followStream((signal) =>
      fetch(`${API_BASE_URL}/api/v1/qa/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          question: currentQuestion,
          thread_id: threadId,
          database,
        }),
        signal,
      }),
    );
  };

  // ── Re-attach to an answer that kept running while we were away ────────

  // `isCancelled` reports whether the user moved on (another thread) while
  // we were looking the answer up.
  const attachToAnswer = async (forThreadId, isCancelled) => {
    let active;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/qa/stream/active?thread_id=${encodeURIComponent(forThreadId)}`,
        { headers: new Headers({ "ngrok-skip-browser-warning": "69420" }) },
      );
      if (isCancelled()) return;
      if (res.status === 404) {
        // Nothing to re-attach to: the answer was lost (e.g. a server restart).
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: t("chat.answerUnavailable"), streaming: false, tools_used: [] },
        ]);
        return;
      }
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      active = await res.json();
      if (!active?.stream_id) throw new Error("Malformed active-answer response");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to look up the running answer:", err);
      return;
    }
    if (isCancelled()) return;

    streamIdRef.current = active.stream_id;
    stoppedByUserRef.current = false;
    idleTimedOutRef.current = false;
    statusLogRef.current = [t("chat.reconnecting")];
    setStatusLog(statusLogRef.current);
    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: "", streaming: true, tools_used: [] },
    ]);
    setLoading(true);
    await followStream((signal) =>
      fetch(`${API_BASE_URL}/api/v1/qa/stream/${encodeURIComponent(active.stream_id)}/events`, {
        headers: { "ngrok-skip-browser-warning": "69420" },
        signal,
      }),
    );
  };

  // ── Follow an answer stream ────────────────────────────────────────────

  // Reads one answer stream (a new question's POST or a re-attach GET) into
  // the trailing streaming bubble until it ends, Stop is clicked, or we leave.
  const followStream = async (openStream) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    // A thread switch can start another stream before this one has settled:
    // only the newest stream may touch the shared loading/error state.
    const isCurrent = () => abortControllerRef.current === controller;

    try {
      const response = await openStream(controller.signal);

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        let idleTimeoutId;
        const idlePromise = new Promise((_, reject) => {
          idleTimeoutId = setTimeout(() => {
            idleTimedOutRef.current = true;
            controller.abort();
            reject(new Error(t("chat.timeoutError")));
          }, STREAM_IDLE_TIMEOUT_MS);
        });
        // Attach a no-op catch so a read() that loses the race and later
        // rejects (e.g. from the abort() above) doesn't surface as an
        // unhandled promise rejection — it does not affect what Promise.race
        // itself resolves/rejects with.
        const readPromise = reader.read();
        readPromise.catch(() => {});
        let done, value;
        try {
          ({ done, value } = await Promise.race([readPromise, idlePromise]));
        } finally {
          clearTimeout(idleTimeoutId);
        }
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";
        for (const block of blocks) {
          for (const line of block.split("\n")) {
            if (line.startsWith("data: ")) {
              try {
                handleStreamEvent(JSON.parse(line.slice(6)));
              } catch {
                /* skip malformed */
              }
            }
          }
        }
      }
    } catch (rawError) {
      // The abort made by the idle timer reaches us as an AbortError (the
      // aborted read() settles before the timer's own rejection): report it as
      // the timeout it is instead of discarding it like a thread-switch abort.
      const error = idleTimedOutRef.current && !stoppedByUserRef.current
        ? new Error(t("chat.timeoutError"))
        : rawError;
      if (error.name === "AbortError") {
        // The ~5s safety-net timeout in handleStop fired because the server
        // never sent a graceful "stopped" event (e.g. it crashed) — finalize
        // the bubble with whatever streamed in so far rather than leaving it
        // stuck showing a typing cursor forever. The pre-existing
        // thread-switch abort (stoppedByUserRef still false here) keeps its
        // original silent-discard behavior — the user already navigated away.
        if (stoppedByUserRef.current) {
          stoppedByUserRef.current = false;
          streamIdRef.current = null;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.streaming) {
              return [
                ...prev.slice(0, -1),
                { ...last, stopped: true, streaming: false },
              ];
            }
            return prev;
          });
        }
        return;
      }
      if (!isCurrent()) return;
      setMessages((prev) => {
        const msgs = [...prev];
        const last = msgs[msgs.length - 1];
        const errMsg = {
          role: "assistant",
          content: t("chat.errorPrefix", { message: error.message }),
          streaming: false,
          tools_used: [],
        };
        if (last?.streaming) return [...msgs.slice(0, -1), errMsg];
        return [...msgs, errMsg];
      });
    } finally {
      if (isCurrent()) {
        setLoading(false);
        onConversationsRefresh();
      }
    }
  };

  // ── Stop mid-answer ────────────────────────────────────────────────────

  const handleStop = () => {
    stoppedByUserRef.current = true;
    const streamId = streamIdRef.current;
    const controller = abortControllerRef.current;

    if (!streamId) {
      // No stream_id yet (stopped before the SSE connection produced its
      // first event) — nothing to tell the server, just abort the fetch.
      controller?.abort();
      return;
    }

    // Safety net: if the server never confirms via a graceful "stopped" SSE
    // event (e.g. it crashed) within this window, fall back to aborting the
    // fetch directly. streamIdRef.current is reset to null by
    // handleStreamEvent as soon as a real done/stopped/error event arrives,
    // so this no-ops if the stream already finished normally by then.
    setTimeout(() => {
      if (streamIdRef.current === streamId) {
        controller?.abort();
      }
    }, STOP_SAFETY_TIMEOUT_MS);

    fetch(`${API_BASE_URL}/api/v1/qa/stream/${streamId}/stop`, {
      method: "POST",
    }).catch(() => {
      /* server unreachable — the safety-net timeout above will abort instead */
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="qa-chat">
      <div className="chat-messages">
        {convLoading ? (
          <div className="conv-loading">{t("chat.loadingConversation")}</div>
        ) : messages.length === 0 ? (
          <div className="empty-chat">
            <h2>{t("chat.askQuestionHeader")}</h2>
            <p>{t("chat.askQuestionSubheader")}</p>
            <div className="example-questions">
              <h3>{t("chat.examplesLabel")}</h3>
              <ul>
                {[
                  "λB of Lücking W12 HLZ 8-0,65?",
                  "Z17 Table 8: insulation density for TV 8+?",
                  "T18 meets λ ≤ 0.18 for Pos. 07.0060?",
                ].map((q) => (
                  <li key={q} onClick={() => setQuestion(q)}>
                    "{q}"
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          messages.map((msg, idx) =>
            msg.role === "assistant" ? (
              <AssistantMessage
                key={idx}
                msg={msg}
                statusLog={msg.streaming ? statusLog : msg.statusLog || []}
                threadId={threadId}
              />
            ) : (
              <div key={idx} className="message user">
                <div className="message-content" dir="auto">{msg.content}</div>
              </div>
            ),
          )
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="chat-input-box">
          <div className="chat-input-left">
            <span className="chat-icon">📎</span>

            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t("chat.questionInputPlaceholder")}
              disabled={loading}
              className="chat-input"
              dir="auto"
            />
          </div>

          {loading ? (
            <button
              type="button"
              onClick={handleStop}
              className="chat-send-btn chat-stop-btn"
              title={t("chat.stopGenerating")}
              aria-label={t("chat.stopGenerating")}
            >
              ■
            </button>
          ) : (
            <button
              type="submit"
              disabled={!question.trim()}
              className="chat-send-btn"
            >
              ➤
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default QAChat;
